import { useCacheRevalidation } from "@prismicio/editor-support/Suspense"
import { type MediaAssetType } from "@prismicio/editor-ui"
import { useEffect, useState } from "react"

import type { EditorConfig } from "../../../EditorConfig"
import {
  searchMedia,
  type SearchMediaArgs,
  searchMediaArgs,
} from "./mediaLibraryData"

interface UseMediaLibraryCacheInvalidationArgs {
  revalidate: () => void
  config: EditorConfig
  keyword: string
  assetType: MediaAssetType
  uploaderId?: string
  tagIds: string[]
}

export function useMediaLibraryCacheInvalidation(
  args: UseMediaLibraryCacheInvalidationArgs,
) {
  const { revalidate, config, keyword, assetType, uploaderId, tagIds } = args
  const {
    assetsApiBaseUrl = undefined,
    repository = "",
    authStrategy = "token",
  } = config.mediaLibrary ? config : {}

  const [isMediaRevalidationInProgress, setIsMediaRevalidationInProgress] =
    useState(false)

  const [isMediaCacheInvalid, setIsMediaCacheInvalid] = useState(false)

  const requestParams = {
    assetsApiBaseUrl,
    repository,
    authStrategy,
    keyword,
    assetType,
    uploaderId,
    tagIds,
    page: 0,
  }

  const { revalidateData } = useCacheRevalidation({
    fetcher: searchMedia,
    requestParams: searchMediaArgs(requestParams),
    validator: (response, cache) => {
      const responseFirstId = response.items[0]?.id ?? ""
      const mediaFirstId = cache.items[0]?.id ?? ""

      return (
        // check if the first media id in the cache is matching the first id on the server
        responseFirstId !== mediaFirstId ||
        // check if the total amount of items in the cache and on the server are equal
        response.total !== cache?.total
      )
    },
    onCacheInvalidated: () => setIsMediaCacheInvalid(true),
    every: 60_000,
  })

  const revalidateWithParams = (args: Partial<SearchMediaArgs>) => {
    revalidateData(searchMediaArgs({ ...requestParams, ...args }))
  }

  // cleanup states to reset any effects and UI based on their state
  useEffect(() => {
    if (isMediaRevalidationInProgress) {
      setIsMediaCacheInvalid(false)
      setIsMediaRevalidationInProgress(false)
    }
  }, [isMediaRevalidationInProgress])

  function revalidateMedia() {
    setIsMediaRevalidationInProgress(true)
    revalidate()
  }
  return {
    isMediaRevalidationInProgress,
    isMediaCacheInvalid,
    revalidateMedia,
    revalidateWithParams,
  }
}
